import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { worklistAPI } from '../services/api';
import toast from 'react-hot-toast';

// Components
import Sidebar from '../components/Sidebar';
import Overview from '../components/Overview';
import LegalGuidance from '../components/LegalGuidance';
import DashboardHome from '../components/DashboardHome';
import DocumentAnalysis from '../components/DocumentAnalysis';
import LegalResearch from '../components/LegalResearch';
import CaseSummary from '../components/CaseSummary';
import ContractReview from '../components/ContractReview';
import LegalBrief from '../components/LegalBrief';
import Compliance from '../components/Compliance';
import Precedents from '../components/Precedents';
import DocumentDraft from '../components/DocumentDraft';
import CostEstimate from '../components/CostEstimate';
import Worklist from '../components/Worklist';
import Settings from '../components/Settings';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState({
    documentsAnalyzed: 0,
    researchQueries: 0,
    casesSummarized: 0,
    contractsReviewed: 0,
    briefsGenerated: 0,
    complianceChecks: 0,
    precedentsResearched: 0,
    documentsDrafted: 0,
    costEstimates: 0,
    totalItems: 0,
    hoursEstimate: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, activityResponse] = await Promise.all([
        worklistAPI.getDashboardStats(),
        worklistAPI.getRecentActivity()
      ]);

      setStats(statsResponse.stats);
      setRecentActivity(activityResponse.recentActivity);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = () => {
    loadDashboardData();
  };

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      
      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={<Overview user={user} />} 
          />
          <Route 
            path="/legal-guidance" 
            element={<LegalGuidance user={user} />} 
          />
          <Route 
            path="/tools" 
            element={
              <DashboardHome 
                user={user} 
                stats={stats} 
                recentActivity={recentActivity} 
                loading={loading} 
              />
            } 
          />
          <Route 
            path="/document-analysis" 
            element={<DocumentAnalysis onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/legal-research" 
            element={<LegalResearch onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/case-summary" 
            element={<CaseSummary onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/contract-review" 
            element={<ContractReview onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/legal-brief" 
            element={<LegalBrief onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/compliance" 
            element={<Compliance onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/precedents" 
            element={<Precedents onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/document-draft" 
            element={<DocumentDraft onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/cost-estimate" 
            element={<CostEstimate onSuccess={refreshStats} stats={stats} loading={loading} />} 
          />
          <Route 
            path="/worklist/*" 
            element={<Worklist />} 
          />
          <Route 
            path="/settings" 
            element={<Settings user={user} onUserUpdate={(updatedUser) => {
              // Update user in App.js if needed
              window.location.reload(); // Simple way to refresh user data
            }} />} 
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;
